/**
 * Cova Chat — space meeting persistence models (Model.Class). Same modifier rules
 * as call.model.ts: server-set-once → DateTimeInsert, mutable → DateTimeUpdate,
 * nullable business timestamps → plain S.NullOr(S.DateTimeUtc). Server-owned
 * identity columns use FieldExcept so RPC input can't forge them.
 *
 * Frontend NEVER imports this file — it consumes the RPC view shapes below.
 *
 * Media/signaling have NO model here: AWS Chime hosts the meeting (MediaRegion
 * ap-southeast-2) and supplies ICE/TURN. We persist only the opaque Chime ids we
 * need to re-address the session; SDP/ICE are never seen, let alone stored.
 *
 * There is NO recording model, deliberately. Meetings are transcribed live
 * (Chime → Amazon Transcribe, ap-southeast-2) and no audio is ever stored, so
 * the transcript segments below are a meeting's only artifact.
 */
import { Schema as S } from "effect"
import { Model } from "@effect/sql"
import {
  ConversationId,
  MeetingId,
  MeetingTranscriptSegmentId,
  MeetingRole,
  MeetingStatus,
  RecurrencePreset,
} from "./chat-enums"

// ============================================================================
// chat_meetings — one row per meeting (scheduled or instant)
// ============================================================================
export class ChatMeeting extends Model.Class<ChatMeeting>("ChatMeeting")({
  id: Model.Generated(MeetingId),
  conversationId: ConversationId,
  organizationId: S.String.pipe(Model.FieldExcept("jsonCreate", "jsonUpdate")), // creator org; audit anchor, NOT the access key
  createdByUserId: S.String.pipe(Model.FieldExcept("jsonCreate", "jsonUpdate")),
  title: S.String,
  status: MeetingStatus,
  scheduledAt: S.NullOr(S.DateTimeUtc), // NULL = instant
  startedAt: S.NullOr(S.DateTimeUtc),
  endedAt: S.NullOr(S.DateTimeUtc),
  // Internal Chime handles — excluded from MeetingView. The join RPC hands the
  // client a Chime `Meeting`+`Attendee` join payload, never these raw ids.
  chimeMeetingId: S.NullOr(S.String),
  mediaRegion: S.String,
  transcriptionEnabled: S.Boolean,
  // ---- scheduling (spec 2026-07-26-001 §6.1) --------------------------------
  /** How long the organiser said it would run. Defaulted in the DB, so every
   *  pre-scheduling row already has a value — never null. */
  durationMinutes: S.Number,
  description: S.NullOr(S.String),
  /** RFC-5545 RRULE on the SERIES PARENT only; null on an occurrence and on a
   *  one-off. Built exclusively by `utils/recurrence.ts`. */
  recurrenceRule: S.NullOr(S.String),
  /** Set on an OCCURRENCE, pointing at its series parent. */
  recurrenceParentId: S.NullOr(MeetingId),
  /** The occurrence's date (YYYY-MM-DD), the other half of the uniqueness
   *  arbiter that makes repeated expansion a no-op. */
  occurrenceDate: S.NullOr(S.String),
  cancelledAt: S.NullOr(S.DateTimeUtc),
  /** Per-occurrence reminder stamp — stops the due-scan re-picking this meeting.
   *  Per-recipient idempotency is the email_outbox dedup_key. Cleared on
   *  reschedule so a new time is reminded again. */
  remindedAt: S.NullOr(S.DateTimeUtc),
  createdAt: Model.DateTimeInsert,
  updatedAt: Model.DateTimeUpdate,
}) {}
export type ChatMeetingJson = typeof ChatMeeting.json.Type

// ============================================================================
// chat_meeting_participants — dynamic membership (people join/leave mid-meeting)
// ============================================================================
export class ChatMeetingParticipant extends Model.Class<ChatMeetingParticipant>(
  "ChatMeetingParticipant",
)({
  id: Model.Generated(S.String),
  meetingId: MeetingId,
  userId: S.String.pipe(Model.FieldExcept("jsonCreate", "jsonUpdate")),
  role: MeetingRole,
  chimeAttendeeId: S.NullOr(S.String),
  /** true = declined transcription consent → mic never published → the words are
   *  never spoken into Transcribe, so they can't reach the transcript. Consent
   *  gates PUBLISH, not membership. */
  listenOnly: S.Boolean,
  joinedAt: Model.DateTimeInsert,
  leftAt: S.NullOr(S.DateTimeUtc),
}) {}
export type ChatMeetingParticipantJson = typeof ChatMeetingParticipant.json.Type

// ============================================================================
// chat_meeting_transcript_segments — a meeting's only artifact
// ============================================================================
/**
 * One finalised Amazon Transcribe result. Partial results are live-only and
 * never persisted — they rewrite themselves as the speaker keeps talking.
 *
 * `speakerUserId` is Chime's attendee attribution (ExternalUserId = users.id),
 * so segments carry real people, not anonymous "Speaker 0/1". Nullable because
 * Transcribe can emit a result it can't attribute.
 */
export class ChatMeetingTranscriptSegment extends Model.Class<ChatMeetingTranscriptSegment>(
  "ChatMeetingTranscriptSegment",
)({
  id: Model.Generated(MeetingTranscriptSegmentId),
  meetingId: MeetingId,
  organizationId: S.String.pipe(Model.FieldExcept("jsonCreate", "jsonUpdate")),
  /** Transcribe's stable id for this result — the idempotency key for relays. */
  resultId: S.String,
  speakerUserId: S.NullOr(S.String),
  content: S.String,
  startTimeMs: S.Number,
  endTimeMs: S.Number,
  confidence: S.NullOr(S.Number),
  createdAt: Model.DateTimeInsert,
}) {}
export type ChatMeetingTranscriptSegmentJson = typeof ChatMeetingTranscriptSegment.json.Type

// ============================================================================
// Viewer projections (wire shapes)
// ============================================================================

/**
 * A meeting row for the space "Meetings" tab. Derived from the model with S.pick
 * so it can't drift, and so `chimeMeetingId` / `organizationId` /
 * `createdByUserId` are structurally excluded from the wire.
 *
 * `isHost` is computed per-CALLER rather than exposing `createdByUserId`: the UI
 * needs to know whether to offer "End for everyone", not who the host is. The
 * server re-derives this on every mutation — this flag only hides a button.
 */
export const MeetingView = S.extend(
  ChatMeeting.json.pipe(
    S.pick(
      "id",
      "conversationId",
      "title",
      "status",
      "scheduledAt",
      "startedAt",
      "endedAt",
      "transcriptionEnabled",
      "createdAt",
    ),
  ),
  S.Struct({ isHost: S.Boolean }),
)
export type MeetingView = typeof MeetingView.Type

/**
 * A SCHEDULED meeting for the upcoming list. Extends `MeetingView` rather than
 * redefining it (`S.extend`, never a hand-rolled duplicate) with the fields only
 * a future meeting has.
 *
 * `recurrencePreset` is sent instead of the raw RRULE: the UI's four presets are
 * all it can render or edit, and shipping the rule would invite a second,
 * divergent parser on the client.
 */
export const ScheduledMeetingView = S.extend(
  MeetingView,
  S.Struct({
    description: S.NullOr(S.String),
    durationMinutes: S.Number,
    recurrencePreset: RecurrencePreset,
    /** True on an occurrence of a series, so the UI can offer
     *  "cancel this one" vs "cancel the series". */
    isRecurringOccurrence: S.Boolean,
    inviteeCount: S.Number,
    organiserName: S.String,
  }),
)
export type ScheduledMeetingView = typeof ScheduledMeetingView.Type

/** A transcript line for the meeting transcript view. */
export const TranscriptSegmentView = ChatMeetingTranscriptSegment.json.pipe(
  S.pick("id", "meetingId", "speakerUserId", "content", "startTimeMs", "endTimeMs"),
)
export type TranscriptSegmentView = typeof TranscriptSegmentView.Type

/**
 * A finalised transcript line relayed from the browser. Chime delivers transcript
 * events to attendees over the data channel — there is no server-side copy of the
 * audio to derive them from — so the client is the only source.
 *
 * Untrusted by construction: the server stamps meetingId/org from the caller's
 * membership, never the body. `chimeAttendeeId` is a self-attribution HINT only —
 * every attendee id is broadcast to the whole room, so a client could name any
 * other attendee. The server therefore attributes a segment to a speaker ONLY
 * when that id resolves to the RELAYING caller; a segment naming anyone else is
 * persisted with no trusted speaker (see ChatMeetingService.appendTranscript).
 * Treat this field as attacker-controlled — it can never authoritatively speak
 * for another user.
 */
export const TranscriptSegmentInput = S.Struct({
  resultId: S.String.pipe(S.minLength(1), S.maxLength(200)),
  chimeAttendeeId: S.NullOr(S.String.pipe(S.maxLength(200))),
  content: S.String.pipe(S.minLength(1), S.maxLength(10_000)),
  startTimeMs: S.Number.pipe(S.nonNegative()),
  endTimeMs: S.Number.pipe(S.nonNegative()),
  confidence: S.NullOr(S.Number),
})
export type TranscriptSegmentInput = typeof TranscriptSegmentInput.Type

/**
 * What the browser needs to join, straight from Chime. `meeting` / `attendee` are
 * the opaque Chime JSON blobs `amazon-chime-sdk-js` consumes
 * (`MeetingSessionConfiguration(meeting, attendee)`) — we never interpret them.
 * `listenOnly` tells the client not to publish a mic track (declined consent).
 */
export const MeetingJoinInfo = S.Struct({
  meetingId: MeetingId,
  meeting: S.Unknown,
  attendee: S.Unknown,
  listenOnly: S.Boolean,
})
export type MeetingJoinInfo = typeof MeetingJoinInfo.Type
